/**
 * The "Ns to make the first move" banner, for ONE side.
 *
 * Its own leaf (unit convention §1 sub-leaf) for the reason the old screen split
 * `FirstMoveTimerBanner` out of `PlayerComponent`
 * (`components/PlayerComponent/FirstMoveTimerBanner.js` L8-17): the countdown
 * ticks, and ticking inside the player row re-rendered the username, the rating
 * and the captured tray with it.
 *
 * WHEN IT SHOWS. The old screen spells the rule out three times, once per row
 * (index.tsx L6880-6883 opponent, L7082 mine):
 *   my row       -> `showAbort && myTurn`
 *   opponent row -> white is me ? `moves.length === 1` : `showAbort && !myTurn`
 * Stripped of the `showAbort` noise all three say ONE thing: the side to move
 * still OWES the game's first move. That rule lives in
 * `@/Store/liveGame/chess/firstMove`, so this and the first-move alarm can never
 * disagree.
 *
 * `showAbort` IS NOT A TERM HERE (P1-4, decision D1), and the old screen's own
 * asymmetry is why. `showAbort` means "MY abort window is still open", so
 * gating BOTH rows on it hid black's still-running countdown from white the
 * instant white moved — which is exactly the bug that was reported. The old
 * opponent row worked around it with the `whitePlayer == username ?
 * moves.length == 1` special case (and only for white); V2 drops the term
 * instead. D1 also settles the other half: the countdown now shows in
 * tournament and arena games too, where `showAbort` is false but the SERVER
 * still aborts at TC/2 without a first move. Abort itself stays hidden there.
 *
 * The number is `ceil((firstMoveDeadlineMs - now) / 1000)`. The old banner
 * derived it from a running clock diff instead (`setFirstMovetimer`,
 * SimpleOptimizedClock L183-190), which is the same countdown by a longer road.
 */
import {useEffect, useMemo, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {myColorMovesFirst} from '@/Store/liveGame/chess/firstMove';
import {useLiveGame} from '@/Store/liveGameStore';
import {id} from '@/Store/liveGame/state';
import type {LiveGameState, Side} from '@/Store/liveGame/state';

const TICK_MS = 250;

export type FirstMoveBannerModel = {
  visible: boolean;
  /** whole seconds left, never negative */
  seconds: number;
};

type BannerInput = {
  armed: boolean;
  deadlineMs: number | null;
};

/** PURE: is the first-move window open FOR THIS SIDE right now? */
export const selectFirstMoveFor =
  (side: Side) =>
  (s: LiveGameState): BannerInput => {
    const sideId = side === 'white' ? s.game.whiteId : s.game.blackId;
    const sideToMove =
      !!s.game.turnUserId && id(s.game.turnUserId) === id(sideId);
    // does THIS side open the game? (a custom start FEN with black to move
    // makes black the one who owes ply 1)
    const opens = myColorMovesFirst({
      myColor: side,
      isBlackTurnFirst: s.game.isBlackTurnFirst,
    });
    const owed = s.game.moves.length < (opens ? 1 : 2);
    return {
      armed:
        s.game.phase === 'playing' &&
        sideToMove &&
        owed &&
        !!s.game.firstMoveDeadlineMs,
      deadlineMs: s.game.firstMoveDeadlineMs,
    };
  };

const secondsLeft = (deadlineMs: number | null): number =>
  deadlineMs ? Math.max(0, Math.ceil((deadlineMs - Date.now()) / 1000)) : 0;

export function useFirstMoveBannerModel(side: Side): FirstMoveBannerModel {
  const selector = useMemo(() => selectFirstMoveFor(side), [side]);
  const {armed, deadlineMs} = useLiveGame(useShallow(selector));
  const [seconds, setSeconds] = useState(() => secondsLeft(deadlineMs));

  useEffect(() => {
    if (!armed) {
      return undefined;
    }
    const tick = () =>
      setSeconds(prev => {
        const next = secondsLeft(deadlineMs);
        return prev === next ? prev : next;
      });
    tick();
    const interval = setInterval(tick, TICK_MS);
    return () => clearInterval(interval);
  }, [armed, deadlineMs]);

  return {visible: armed && seconds >= 0, seconds: armed ? seconds : 0};
}
